import React, { useEffect, useState } from 'react';
import './AppSettings.scss';
import TwTabs from '../../../components/TwTabs/TwTabs';
import TwTab from '../../../components/TwTab/TwTab';
import { useLocation } from 'react-router-dom';
import GeneralSettings from './GeneralSettings/GeneralSettings';
import styled from 'styled-components';
import LoadNotificationSettings from './LoadNotificationSettings/LoadNotificationSettings';
import OrganizationSettings from './OrganizationSettings/OrganizationSettings';
import useFeatureFlags from '../../../providers/FeatureFlags/FeatureFlags.provider';
import InvoicingAndAccounting, { SettingsQuery } from '../Invoicing/Invoicing';
import { TabPanel } from '../../../components/TwTabPanel/TwTabPanel';
import useAuth from '../../../providers/Auth/Auth.provider';
import { USER_ROLE } from '../../../providers/User/User.model';
import DocumentSettings from './DocumentSettings/DocumentSettings';
import { RoutingSettings } from '../Routing/Routing';

interface LocationState {
  state: { tab: number };
}

interface TabProps {
  label: string;
  role: string;
  disableRipple: boolean;
  panel: React.ReactElement;
  visible: boolean;
}

export const AppSettingsTabs = styled(TwTabs)({
  width: '100%',
  alignSelf: 'baseline',

  '& .MuiTabs-scroller': {
    height: '50px',
    borderBottom: '1px solid #E5E7EB'
  },
  '& .MuiTabs-scroller .MuiTabs-indicator': {
    backgroundColor: '#3A4DE9 !important',
    height: '1px !important',
    border: '1.3px solid #3A4DE9 !important',
    borderRadius: '10px 10px 0 0'
  },
  '& .Mui-selected': {
    textTransform: 'none',
    color: '#1d212c !important'
  }
});

const AppSettings: React.FC<SettingsQuery> = ({ query }): JSX.Element => {
  const {
    data: { userData }
  } = useAuth();
  const {
    enableOrganizations,
    enableFactoring,
    enablePcMiler,
    enableHideGoogleMapsAndRoutingCalculations
  } = useFeatureFlags();
  const [tab, setTab] = useState(0);
  const location = useLocation();

  useEffect(() => {
    setTab((location as LocationState)?.state?.tab || 0);
    window.history.replaceState({}, document.title);
  }, [location.state]);

  const handleTabChange = (e: React.SyntheticEvent, tab: number): void => {
    setTab(tab);
  };

  const tabs: TabProps[] = [
    {
      label: 'Invoicing & accounting',
      role: 'tab',
      disableRipple: true,
      panel: <InvoicingAndAccounting query={query} />,
      visible: true
    },
    {
      label: 'Driver',
      role: 'tab',
      disableRipple: true,
      panel: <GeneralSettings category={'driver'} />,
      visible: [USER_ROLE.ADMIN, USER_ROLE.OWNER].includes(userData!.role!)
    },
    {
      label: 'Order notifications',
      role: 'tab',
      disableRipple: true,
      panel: <LoadNotificationSettings />,
      visible: true
    },
    {
      label: 'Organization',
      role: 'tab',
      disableRipple: true,
      panel: <OrganizationSettings />,
      visible: enableOrganizations
    },
    {
      label: 'General',
      role: 'tab',
      disableRipple: true,
      panel: <GeneralSettings category={null} />,
      visible: true
    },
    {
      label: 'Documents',
      role: 'tab',
      disableRipple: true,
      panel: <DocumentSettings />,
      visible: true
    },
    {
      label: 'Mapping',
      role: 'tab',
      disableRipple: true,
      panel: <RoutingSettings />,
      visible: enablePcMiler && !enableHideGoogleMapsAndRoutingCalculations
    }
  ];

  return (
    <div className="app-settings">
      <h1 className="app-settings__title">App settings</h1>
      <AppSettingsTabs
        variant="standard"
        value={tab}
        onChange={handleTabChange}
        aria-label="TwTabs"
        role="TwTabs"
      >
        {/* Tabs rendered */}
        {tabs
          .filter((tab) => tab.visible)
          .map((tab, index) => (
            <TwTab key={index} label={tab.label} role={tab.role} disableRipple />
          ))}
      </AppSettingsTabs>

      {/* Tab Panels rendered */}
      {tabs
        .filter((tab) => tab.visible)
        .map((tabItem, index) => (
          <TabPanel key={index} value={tab} index={index}>
            {tabItem.panel}
          </TabPanel>
        ))}
    </div>
  );
};

export default AppSettings;
